/**
 * Split an email body into "what this person wrote" and "the quoted history".
 *
 * A reply card in a thread view shows the quoted original a second time — the
 * message it quotes is already rendered as its own card directly above, usually
 * with the sender's full HTML signature (photo, logo, phone block). One reply
 * to a signed message produces a card several screens tall whose useful content
 * is the first line.
 *
 * ## Nothing is ever discarded
 *
 * These functions return BOTH halves and the caller re-renders the original
 * string when the reader expands the quote, so expanded output is byte-identical
 * to rendering the untouched body. Stripping at ingest time was rejected for the
 * same reason: `ingest_message` inserts once and returns early on a duplicate
 * key, and `_fetch_message` returns null once Graph tombstones the message, so a
 * bad strip would be unrecoverable. Quote detection has real false positives — a
 * forwarded contract, body copy that happens to contain "From:" — and the stored
 * body also backs list search and forwarding.
 *
 * ## Why a DOM parse rather than a regex
 *
 * Every reliable marker is an element-level fact: an `id`, a `class`, a
 * `type="cite"`, a style substring. A string regex cannot tell an attribute from
 * body copy, and cutting at a character offset leaves unbalanced HTML in BOTH
 * halves — which matters because both halves are rendered into a sandboxed
 * iframe. The DOM is used strictly as a parser and tree editor: no
 * `getComputedStyle`, no layout, nothing that only works in a real browser and
 * cannot be pinned by a fixture.
 *
 * The input is expected to be ALREADY sanitized (see `sanitizeEmailHtml`). This
 * module is not a security boundary — the order is always sanitize, then split.
 */

/**
 * Structural markers, in no particular priority: the EARLIEST match in document
 * order wins, because a reply stacked on a reply carries several of them and the
 * outermost one is the true boundary.
 *
 * Each is scoped to an id, a class or an attribute, so a false positive needs
 * the sending client to have used the marker for something other than a quote.
 */
export const QUOTE_MARKERS = [
    // Exchange server-side createReply, and Outlook Web. This is what the
    // unified inbox's own replies carry, because the draft is composed by Graph.
    '#divRplyFwdMsg',
    // Outlook desktop marks where new content ends and the quote begins.
    '#appendonsend',
    // Gmail.
    '.gmail_quote',
    '.gmail_quote_container',
    '.gmail_attr',
    // Thunderbird.
    '.moz-cite-prefix',
    // Apple Mail, and the generic RFC-ish convention.
    'blockquote[type="cite"]',
    // Yahoo, Proton, Zoho.
    '.yahoo_quoted',
    '.protonmail_quote',
    '.zmail_extra',
    // Our own quotes. `ncrm-quote` is stamped going forward; the border-left
    // matcher is kept permanently for mail already sent without it.
    '.ncrm-quote',
    'blockquote[style*="border-left"]',
];

/**
 * An attribution line — "From: … Sent: … To: …", "On <date> X wrote:".
 *
 * Only ever used to CONFIRM a weaker structural signal (a bare `<hr>`, a
 * Word-style top border). Never used on its own: "From:" appears in ordinary
 * body copy often enough that matching it alone would truncate real messages.
 *
 * English-only, deliberately. A localised Outlook ("Von:", "Gesendet:") falls
 * through to no split, which shows the whole body exactly as today. Adding
 * locales without captured samples would trade a harmless miss for a false
 * positive that eats someone's message.
 */
const ATTRIBUTION_RE = /(^|\n)\s*(From|Sent|To|Subject)\s*:\s*\S|On\s.{4,80}\swrote\s*:|-{2,}\s*Original Message\s*-{2,}/i;

const MAX_ATTRIBUTION_SCAN = 400;

function hasAttribution(node) {
    const text = (node.textContent || '').slice(0, MAX_ATTRIBUTION_SCAN);
    return ATTRIBUTION_RE.test(text);
}

/** The ancestor of `node` that is a direct child of `body`. */
function topLevelAncestor(node, body) {
    let current = node;
    while (current && current.parentNode && current.parentNode !== body) {
        current = current.parentNode;
    }
    return current && current.parentNode === body ? current : null;
}

/**
 * A node that carries no visible content — whitespace, an empty div, a rule.
 *
 * Used only to pull separators back into the quoted half, so the visible half
 * does not end with an orphan horizontal rule that used to introduce a quote
 * that is now hidden.
 */
function isSeparator(node) {
    if (node.nodeType === 3) return !(node.textContent || '').trim();
    if (node.nodeType !== 1) return true;
    const tag = node.tagName.toLowerCase();
    if (tag === 'hr' || tag === 'br') return true;
    return !(node.textContent || '').trim() && !node.querySelector('img');
}

function hasVisibleContent(nodes) {
    return nodes.some(node => {
        if (node.nodeType === 3) return Boolean((node.textContent || '').trim());
        if (node.nodeType !== 1) return false;
        if ((node.textContent || '').trim()) return true;
        return Boolean(node.querySelector && node.querySelector('img'));
    });
}

function serialize(nodes) {
    return nodes
        .map(node => (node.nodeType === 1 ? node.outerHTML : node.textContent || ''))
        .join('');
}

/**
 * Find the boundary element, earliest in document order.
 *
 * Tier A is structural and trusted on its own. Tier B needs an attribution line
 * beside it, because a `<hr>` or a top border is far too common to trust alone.
 */
function findBoundary(body) {
    const tierA = Array.from(body.querySelectorAll(QUOTE_MARKERS.join(',')));
    if (tierA.length) {
        const el = tierA[0];
        const marker = QUOTE_MARKERS.find(sel => {
            try {
                return el.matches(sel);
            } catch {
                return false;
            }
        });
        return { el, marker: marker || 'structural' };
    }

    // Tier B — a Word-style bordered block that also reads like an attribution.
    const bordered = Array.from(body.querySelectorAll('div[style*="border-top"]'));
    for (const el of bordered) {
        if (hasAttribution(el)) return { el, marker: 'border-top+attribution' };
    }

    // Tier B — a top-level rule whose following content reads like an attribution.
    const children = Array.from(body.childNodes);
    for (let i = 0; i < children.length; i += 1) {
        const node = children[i];
        if (node.nodeType !== 1 || node.tagName.toLowerCase() !== 'hr') continue;
        const rest = children.slice(i + 1);
        if (rest.length && ATTRIBUTION_RE.test(serialize(rest).slice(0, MAX_ATTRIBUTION_SCAN))) {
            return { el: node, marker: 'hr+attribution' };
        }
    }

    return null;
}

/**
 * Split sanitized HTML into `{ visible, quoted, marker }`.
 *
 * Fails OPEN in three cases, each returning the input untouched as `visible`
 * with an empty `quoted`, because showing too much is always recoverable by the
 * reader and showing too little is not:
 *
 * - no boundary found;
 * - no `DOMParser` available;
 * - nothing visible before the boundary — a bare forward, where hiding
 *   everything would render an empty white card.
 */
export function splitQuotedHtml(html) {
    const empty = { visible: html || '', quoted: '', marker: null };
    if (!html || typeof DOMParser === 'undefined') return empty;

    let doc;
    try {
        doc = new DOMParser().parseFromString(html, 'text/html');
    } catch {
        return empty;
    }
    const body = doc && doc.body;
    if (!body) return empty;

    const boundary = findBoundary(body);
    if (!boundary) return empty;

    const anchor = topLevelAncestor(boundary.el, body);
    if (!anchor) return empty;

    const children = Array.from(body.childNodes);
    let cut = children.indexOf(anchor);
    if (cut < 0) return empty;

    // Pull separators immediately before the boundary into the quoted half, so
    // the visible half does not end with an orphan rule introducing a quote that
    // is no longer shown.
    while (cut > 0 && isSeparator(children[cut - 1])) cut -= 1;

    const visibleNodes = children.slice(0, cut);
    const quotedNodes = children.slice(cut);

    if (!hasVisibleContent(visibleNodes)) return empty;

    return {
        visible: serialize(visibleNodes),
        quoted: serialize(quotedNodes),
        marker: boundary.marker,
    };
}

/**
 * Plain-text markers, for `bodyPreview` — which is what the list snippet shows.
 *
 * Graph's `bodyPreview` is a 255-character plaintext excerpt with the HTML
 * stripped, so the structural markers above are gone by the time it reaches us
 * and the quote bleeds into the snippet as
 * `"Yoyoyoyo ________ From: Owen Banan Sent: …"`.
 */
const TEXT_QUOTE_PATTERNS = [
    /_{5,}/,                            // Outlook's separator rule, flattened
    /(^|\n)\s*-{2,}\s*Original Message\s*-{2,}/i,
    /(^|\n)\s*On\s.{4,120}\swrote\s*:/i,
    /(^|\n)\s*From\s*:\s*\S/i,
    /(^|\n)\s*>{1,}\s/,                 // classic quote prefix
];

/** Split plain text into `{ visible, quoted }` at the earliest quote marker. */
export function splitQuotedText(text) {
    const source = text || '';
    let cut = -1;
    for (const pattern of TEXT_QUOTE_PATTERNS) {
        const match = source.match(pattern);
        if (match && match.index !== undefined && (cut < 0 || match.index < cut)) {
            cut = match.index;
        }
    }
    if (cut < 0) return { visible: source, quoted: '' };
    const visible = source.slice(0, cut);
    // Same fail-open rule as the HTML splitter: never hide everything.
    if (!visible.trim()) return { visible: source, quoted: '' };
    return { visible, quoted: source.slice(cut) };
}

/** Either half, whichever the body actually is. */
export function splitQuotedBody({ html, text } = {}) {
    if (html) return splitQuotedHtml(html);
    const split = splitQuotedText(text || '');
    return { ...split, marker: split.quoted ? 'text' : null };
}

/**
 * A short, quote-free preview for the list's Snippet column.
 *
 * Replaces the local `stripHtml` the table used, which truncated at 120
 * characters without removing the quote — so a one-word reply showed the
 * previous message's header block instead of the reply.
 */
/** The handful of named entities that actually turn up in mail previews. */
const NAMED_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
};

/**
 * Decode HTML entities in text that is about to be rendered as plain text.
 *
 * Gmail's `snippet` — which is what the personal inbox stores in `body` — is
 * HTML-escaped, so a preview rendered verbatim reads `didn&#39;t` and
 * `Q&amp;A`. Stripping tags without decoding entities leaves exactly that.
 *
 * Deliberately not `innerHTML` on a scratch element: this runs over untrusted
 * mail content, and assigning it to a live DOM node would execute anything
 * that survives the tag strip. A bounded regex cannot.
 */
function decodeEntities(text) {
    return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity) => {
        if (entity[0] === '#') {
            const codePoint = entity[1] === 'x' || entity[1] === 'X'
                ? parseInt(entity.slice(2), 16)
                : parseInt(entity.slice(1), 10);
            if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
                return match;
            }
            try {
                return String.fromCodePoint(codePoint);
            } catch {
                return match;
            }
        }
        const named = NAMED_ENTITIES[entity.toLowerCase()];
        return named === undefined ? match : named;
    });
}

export function previewText(body, limit = 120) {
    if (!body) return '';
    // Strip tags first, then decode — the other order would turn an encoded
    // `&lt;script&gt;` into a real tag for the stripper to act on.
    const plain = decodeEntities(String(body).replace(/<[^>]*>/g, ' '));
    const { visible } = splitQuotedText(plain);
    return visible.replace(/\s+/g, ' ').trim().slice(0, limit);
}
